/**
 * auth.js - Authentication Handlers
 */

declare const supabaseClient: any;
declare function showToast(msg: string): void;
declare function showView(view: string): void;
declare function switchTab(tab: string): Promise<void>;
declare function loadUserHistory(): void;
declare function loadUserPrompts(): Promise<void>;
declare function updateDrawerEmail(): void;

// Use window.currentUser directly to share state with app.ts
function getCurrentUser() { return (window as any).currentUser; }
function setCurrentUser(user: any) { (window as any).currentUser = user; }

let authMode = 'login';

function showAuthOverlay() {
    const overlay = document.getElementById('authOverlay');
    if (overlay) overlay.style.display = 'flex';
}

function hideAuthOverlay() {
    const overlay = document.getElementById('authOverlay');
    if (overlay) overlay.style.display = 'none';
}

function switchAuth(mode: string) {
    authMode = mode;
    const title = document.getElementById('authTitle');
    const subtitle = document.getElementById('authSubtitle');
    const btn = document.getElementById('authBtn') as HTMLButtonElement;
    const loginTab = document.getElementById('loginTab');
    const signupTab = document.getElementById('signupTab');

    if (!title || !subtitle || !btn || !loginTab || !signupTab) return;

    if (mode === 'login') {
        title.textContent = 'Welcome Back';
        subtitle.textContent = 'Sign in to access your AVI (Ai-Tools)';
        btn.textContent = 'Login';
        loginTab.classList.add('active');
        signupTab.classList.remove('active');
    } else {
        title.textContent = 'Create Account';
        subtitle.textContent = 'Join the future of productivity';
        btn.textContent = 'Sign Up';
        loginTab.classList.remove('active');
        signupTab.classList.add('active');
    }
}

async function handleAuth() {
    const email = (document.getElementById('authEmail') as HTMLInputElement)?.value;
    const pass = (document.getElementById('authPass') as HTMLInputElement)?.value;
    const errorDiv = document.getElementById('authError');
    const btn = document.getElementById('authBtn') as HTMLButtonElement;

    if (!email || !pass) {
        if (errorDiv) {
            errorDiv.textContent = 'Please fill in all fields';
            errorDiv.style.display = 'block';
        }
        return;
    }

    try {
        if (errorDiv) errorDiv.style.display = 'none';
        if (btn) {
            btn.disabled = true;
            btn.textContent = 'Processing...';
        }

        if (!supabaseClient) throw new Error("Supabase not initialized");

        let result;
        if (authMode === 'login') {
            result = await supabaseClient.auth.signInWithPassword({ email, password: pass });
        } else {
            result = await supabaseClient.auth.signUp({ email, password: pass });
        }

        if (result.error) throw result.error;

        setCurrentUser(result.data.user);
        localStorage.setItem('user-email', email);
        showToast(authMode === 'login' ? 'Logged in successfully' : 'Account created! Please check your email.');

        // Update drawer email in sidebar
        const drawerEmail = document.getElementById('drawerUserEmail');
        if (drawerEmail) drawerEmail.textContent = result.data.user?.email || email;
        
        // Update email in profile context menu
        const menuEmail = document.getElementById('menuUserEmail');
        if (menuEmail) menuEmail.textContent = result.data.user?.email || email;

        hideAuthOverlay();
        showView('tool');
        switchTab('helper');
        loadUserHistory();
        loadUserPrompts();
    } catch (err: any) {
        if (errorDiv) {
            errorDiv.textContent = err.message;
            errorDiv.style.display = 'block';
        }
    } finally {
        if (btn) {
            btn.disabled = false;
            btn.textContent = authMode === 'login' ? 'Login' : 'Sign Up';
        }
    }
}

async function handleLogout() {
    try {
        if (supabaseClient) await supabaseClient.auth.signOut();
        localStorage.removeItem('user-email');
        showView('landing');
        showToast('Logged out');
    } catch (err) {
        console.error('Logout error:', err);
    }
}

function continueAsGuest() {
    showToast('Guest mode is disabled. Please log in or create an account.');
    showAuthOverlay();
}

async function showChangePassword() {
    const email = localStorage.getItem('user-email');
    if (!email) return showToast('Please log in first');

    try {
        if (!supabaseClient) throw new Error("Supabase not initialized");
        const { error } = await supabaseClient.auth.resetPasswordForEmail(email);
        if (error) throw error;
        showToast(`Password reset link sent to ${email}`);
    } catch (err: any) {
        showToast(`Error: ${err.message}`);
    }
}

function comingSoon(feature: string) {
    showToast(`${feature} is coming soon in v1.2!`);
}

// Export to window
(window as any).showAuthOverlay = showAuthOverlay;
(window as any).hideAuthOverlay = hideAuthOverlay;
(window as any).switchAuth = switchAuth;
(window as any).handleAuth = handleAuth;
(window as any).handleLogout = handleLogout;
(window as any).continueAsGuest = continueAsGuest;
(window as any).showChangePassword = showChangePassword;
(window as any).comingSoon = comingSoon;
